import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { I18n } from 'react-redux-i18n'

import { updateActionsErrors } from 'redux/smartchat/details/actions'
import { setAlternativesActionsErrors } from 'redux/smartchat/alternatives/actions'
import ProcessSearchViewUI from './ProcessSearchViewUI'
import { getDataOptions } from 'components/SmartChatView/utils'

const ProcessSearchView = ({ renderType, label, placeholder, dataWay, currentValue, onChange, error }) => {
  const { header, modalActionRef } = useSelector(store => ({
    header: store.smartchatHeader.header,
    modalActionRef: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesModalActionRef.find(modalActionRef => modalActionRef.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.modalActionRef
  }), shallowEqual)
  const dispatch = useDispatch()
  const [processesOptions, setProcessesOptions] = useState(null)
  const [value, setValue] = useState({})
  const [dataToExport, setDataToExport] = useState(currentValue || {})
  const [loadingProcessesOptions, setLoadingProcessesOptions] = useState(false)
  const [previewIntegrity, setPreviewIntegrity] = useState({})

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && !processesOptions) getProcessesOptions()

    return () => isSubscribed = false
  }, [])

  // Current data
  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (!currentValue || !Object.keys(currentValue).length) return

      if (renderType === 'action') {
        if (modalActionRef.mode !== 'update') return
        onChange(currentValue)
      }

      const error = { processCapstone: {} }
      const updatedValue = { ...value }
      const updatedPreviewIntegrity = { ...previewIntegrity }

      // Timeout
      if (currentValue.capstoneTimeout) {
        if (updatedPreviewIntegrity.timer) updatedPreviewIntegrity.timer = null
        updatedValue.timer = { time: currentValue.capstoneTimeout, unit: 'seconds' }
      } else {
        // Error
        if (renderType === 'action') {
          if (updatedValue.timer !== null) error.processCapstone.timer = I18n.t('smartchat_tooltip_warning_notFound')
        } else {
          updatedPreviewIntegrity.timer = I18n.t('smartchat_tooltip_warning_notFound')
        }
      }

      if (processesOptions) {
        let option = null

        // Complete process
        option = processesOptions.find(process => process.value === currentValue.capstoneCompleteProcess)
        if (option) {
          if (updatedPreviewIntegrity.completeProcess) updatedPreviewIntegrity.completeProcess = null
          updatedValue.completeProcess = option
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.completeProcess !== null) error.processCapstone.completeProcess = I18n.t('smartchat_tooltip_warning_notFound')
          } else {
            updatedPreviewIntegrity.completeProcess = I18n.t('smartchat_tooltip_warning_notFound')
          }
        }

        // Partial process
        option = processesOptions.find(process => process.value === currentValue.capstonePartialProcess)
        if (option) {
          if (updatedPreviewIntegrity.partialProcess) updatedPreviewIntegrity.partialProcess = null
          updatedValue.partialProcess = option
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.partialProcess !== null) error.processCapstone.partialProcess = I18n.t('smartchat_tooltip_warning_notFound')
          } else {
            updatedPreviewIntegrity.partialProcess = I18n.t('smartchat_tooltip_warning_notFound')
          }
        }
      }

      // Update local data
      setValue(updatedValue)

      // Error reports
      if (renderType === 'action') {
        if (Object.keys(error.processCapstone).length) dataWay?.type === 'alternatives'
          ? dispatch(setAlternativesActionsErrors({ alternativesId: dataWay.alternativesId, actionsErrors: error }))
          : dispatch(updateActionsErrors(error))
      } else {
        if (Object.keys(updatedPreviewIntegrity).length) setPreviewIntegrity(updatedPreviewIntegrity)
      }
    }

    return () => isSubscribed = false
  }, [currentValue, processesOptions])

  const getProcessesOptions = async () => {
    setLoadingProcessesOptions(true)
    try {
      const { processesOptions } = await getDataOptions({ requestedData: ['processesOptions'], params: { headerId: header._id } })
      setProcessesOptions(processesOptions)
    } finally {
      setLoadingProcessesOptions(false)
    }
  }

  const onChangeInner = ({ fieldType, fieldData }) => {
    const updatedValue = { ...value }
    const updatedDataToExport = { ...dataToExport }

    // Data to export
    if (fieldType === 'timer') {
      if (fieldData.time) {
        updatedValue.timer = fieldData
        updatedDataToExport.capstoneTimeout = fieldData.time
      } else {
        updatedValue.timer = null
        updatedDataToExport.capstoneTimeout = null

        // Clear later fields and data
        if (updatedValue.completeProcess) {
          updatedValue.completeProcess = null
          updatedDataToExport.capstoneCompleteProcess = null
        }
        if (updatedValue.partialProcess) {
          updatedValue.partialProcess = null
          updatedDataToExport.capstonePartialProcess = null
        }
      }
    }
    if (fieldType === 'completeProcess' && fieldData) {
      updatedValue.completeProcess = fieldData
      updatedDataToExport.capstoneCompleteProcess = fieldData.value
    }
    if (fieldType === 'partialProcess' && fieldData) {
      updatedValue.partialProcess = fieldData
      updatedDataToExport.capstonePartialProcess = fieldData.value
    }

    // Update local data
    setValue(updatedValue)
    setDataToExport(updatedDataToExport)

    // Export the data
    onChange(updatedDataToExport)
  }

  return (
    <ProcessSearchViewUI
      renderType={renderType}
      label={label}
      placeholder={placeholder}
      processesOptions={processesOptions}
      value={value}
      onChangeInner={onChangeInner}
      loadingProcessesOptions={loadingProcessesOptions}
      error={error}
      previewIntegrity={previewIntegrity}
    />
  )
}

export default ProcessSearchView
